<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, onAppEvent } from '@/Utils/appEventBus'
import { computed, ref, onMounted, onBeforeUnmount, getCurrentInstance, watch, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import _ from "lodash";
import moment from "moment";
import Spinner from "@/components/ui/Spinner.vue";
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import IconBase from "@/components/icon/IconBase.vue";
import IconPlus from "@/components/icon/IconPlus.vue";
import IconFolder from "@/components/icon/IconFolder.vue";
import IconHosting from "@/components/icon/IconHosting.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import StreamService from "@/services/StreamService";
import AddVodModal from "@/components/modals/AddVodModal.vue";
import SubscriptionService from "@/services/SubscriptionService";
import VODFolderGrid from "@/components/VOD/VODFolderGrid.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import { useRouter } from 'vue-router'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const router = useRouter()

const noAccess = ref(false)
const streams = ref([])
const isVodPaid = ref(false)
const isVodTrial = ref(false)
const emptyVodSub = ref(true)
const vodTrialExpiredIn = ref(0) // days

const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const vodFiles = computed(() => store.getters['Streams/vodFiles'])
const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const maxVodFolders = computed(() => store.getters['User/maxVodFolders'])
const maxVodStorage = computed(() => store.getters['User/maxVodStorage'])

/**
 * A trial notice belongs to accounts that are actually on a trial. A paying
 * account can still carry a zero-charge VOD entry — the hosting included in
 * its plan — and the flags below read "no charge" as "trial", which is how a
 * subscriber on a higher tier ended up being told their trial was ending.
 */
const hasPaidPlan = computed(() => (store.getters['User/subscriptions'] || [])
  .some((sub) => sub?.enabled && Number(sub?.package?.baseCharge) > 0))

const userSubscription = computed(() => {
  return {
    subscription: baseSubscription.value,
    addonSubscriptions: addonSubscriptions.value,
    maxVodItems: maxVodFolders.value,
    maxVodStorage: maxVodStorage.value
  }
})

const allVodStreams = computed(() => {
  return _.orderBy(streams.value, ['creationTime'], ['desc']).filter((stream) => {
    if (stream.type !== 'vod') return false;
    return true
  });
})

const userloaded = computed(() => {
  return store.state.User.userloaded;
})

const activeTeam = computed(() => {
  return store.state.User.activatedTeam;
})

const memberPermission = computed(() => {
  return activeTeam.value && store.state.User.memberPermission;
})

const userEmail = computed(() => {
  return store.state.User.email;
})

const storageUsedBytes = computed(() =>
  vodStreams.value.reduce((accum, item) => accum + (item.totalBytes || 0), 0)
)

const onFoldersRefresh = async () => {
  await store.dispatch('Streams/getStreams', true)
}

watch(
  () => memberPermission.value,
  async (configNew, configOld) => {
    const permission = await checkPermissions();

    if (!activeTeam.value || permission) {
      noAccess.value = false;
    } else {
      noAccess.value = true;
    }
  },
  { deep: true }
)

// Subscription data (base/addon subs, quotas) loads asynchronously and often
// after onMounted's first getVodSubExpiredDays() call, so recompute the trial
// flags whenever it changes, otherwise isVodTrial/emptyVodSub stay stale and
// the trial message + Create Folder button never appear for trial accounts.
watch(
  userSubscription,
  () => getVodSubExpiredDays(),
  { deep: true }
)

onMounted(async () => {
  const streamsReady = store.getters['Streams/isLoaded']
  if (!streamsReady) {
    store.dispatch('Ui/setLoadingStatus', true);
    await store.dispatch('Streams/getStreams');
  } else {
    store.dispatch('Ui/setLoadingStatus', false);
  }
  if(memberPermission.value && !memberPermission.value.hostingManage) {
    noAccess.value = true;
  }
  getVodSubExpiredDays();
  store.dispatch('Ui/setLoadingStatus', false);
  store.dispatch("Ui/getAllTags");
  if (!activeTeam.value) await store.dispatch('User/loadUser', null, { root: true });
})

const onTeamChanged = () => {
  const redirect = checkRedirect()
  if (redirect) {
    router.push({ path: '/' });
  }
};

onBeforeUnmount(onAppEvent(APP_EVENTS.TEAM_CHANGED, onTeamChanged))

const checkPermissions = () => {
  return memberPermission.value && (memberPermission.value.hostingManage || memberPermission.value.hostingDelete);
}

const activateTrial = async () => {
  const subCat = "vod";
  try {
    await SubscriptionService.requestTrial(subCat);
    await store.dispatch('User/loadUser', true, { root: true });
    getVodSubExpiredDays();
    notify({
      group: "success",
      text: `Video Hosting Trial is activated successfully`,
    });
  } catch (e) {
    console.error(e);
    notify({
      group: "error",
      text: `Unable to activate your Video Hosting trial. Please try again.`,
    });
    return;
  }
}

const getVodSubExpiredDays = () => {
  // Reset before recomputing, this runs again whenever the subscription data
  // loads/changes, so stale flags from an earlier (empty) run must be cleared.
  isVodTrial.value = false;
  isVodPaid.value = false;
  emptyVodSub.value = true;
  vodTrialExpiredIn.value = 0;
  let originalVodSub;
  let paidSub;
  let trialSub;
  let hasMaxVodItems = false;
  if (_.isArray(userSubscription.value)) {
    userSubscription.value.forEach((sub) => {
      if (sub.category === "vod") emptyVodSub.value = false;
      if (sub.category === "vod" && sub.package.baseCharge === 0) {
        trialSub = sub;
        isVodTrial.value = true;
      }
      if (
        sub.category === "vod" &&
        sub.package.baseCharge > 0 &&
        sub.enabled
      ) {
        originalVodSub = sub;
      }
      if (
        sub.category !== "vod" &&
        sub.package.definition.maxVodItems &&
        parseInt(sub.package.definition.maxVodItems, 10) > 0
      ) {
        paidSub = sub;
        if (sub.enabled === true) hasMaxVodItems = true;
      }
    });
  }
  if (_.isObject(userSubscription.value)) {
    userSubscription.value.addonSubscriptions.forEach((sub) => {
      if (sub.category === "vod") emptyVodSub.value = false;
      if (sub.category === "vod" && sub.package.baseCharge === 0) {
        trialSub = sub;
        isVodTrial.value = true;
      }
      if (
        sub.category === "vod" &&
        sub.package.baseCharge > 0 &&
        sub.enabled
      ) {
        originalVodSub = sub;
      }
      if (
        sub.category !== "vod" &&
        sub.package.definition.maxVodItems &&
        parseInt(sub.package.definition.maxVodItems, 10) > 0
      ) {
        paidSub = sub;
        if (sub.enabled === true) hasMaxVodItems = true;
      }
    });
  }
  if (trialSub) {
    vodTrialExpiredIn.value = moment(trialSub.cend).diff(moment(), "day");
  }

  if (paidSub) {
    isVodPaid.value = true;
    let paidSubExpiredIn = moment(paidSub.cend).diff(moment(), "day");

    // Compare with the previous expire day to get which is available
    if (trialSub && vodTrialExpiredIn.value > paidSubExpiredIn) {
      paidSubExpiredIn = vodTrialExpiredIn.value;
      isVodPaid.value = false;
    }

    vodTrialExpiredIn.value = paidSubExpiredIn;

    if (hasMaxVodItems) {
      isVodTrial.value = false;
      vodTrialExpiredIn.value = 1;
    }
  }

  if (originalVodSub) {
    isVodPaid.value = true;
    const originalVodSubExpiredIn = moment(originalVodSub.cend).diff(
      moment(),
      "day"
    );

    if (originalVodSubExpiredIn > vodTrialExpiredIn.value) {
      vodTrialExpiredIn.value = originalVodSubExpiredIn;
      isVodTrial.value = false;
    }
  }
  return vodTrialExpiredIn.value;
}

const checkManagePermissions = () => {
  if (!memberPermission.value || !activeTeam.value) return true;
  let hostingManage = (memberPermission.value && memberPermission.value.hostingManage);
  return hostingManage;
}

const checkRedirect = () => {
  let parts = window.location.pathname.split('/');
  let redirect = false;
  parts.map(function (p) {
    if ((p == 'multistreams') || (p == 'scheduled') || (p == 'videos') || (p == 'livestreams')) {
      redirect = true;
    }
  });
  return redirect;
}
const showVodAddModal = () => {
  if (!checkManagePermissions()) return;
  showModal('modal-add-vod');
}

const onNewStream = async () => {
  await store.dispatch('Streams/getStreams', true);
}

// Empty-state inline folder creation (same flow as the grid's popover)
const creatingFirstFolder = ref(false);
const firstFolderName = ref('');
const firstFolderProcessing = ref(false);
const firstFolderInput = ref(null);
const startFirstFolder = () => {
  if (!checkManagePermissions() || vodTrialExpiredIn.value < 0) return;
  creatingFirstFolder.value = true;
  firstFolderName.value = '';
  nextTick(() => firstFolderInput.value?.focus?.());
};
const cancelFirstFolder = () => { creatingFirstFolder.value = false; };
const confirmFirstFolder = async () => {
  const name = firstFolderName.value.trim();
  if (!name || firstFolderProcessing.value) return;
  firstFolderProcessing.value = true;
  try {
    await store.dispatch('Regions/getRegions', 'vod');
    const regions = store.getters['Regions/getRegionsByCategory']('vod') || [];
    await StreamService.addStream(name, regions[0]?._id, 'vod');
    await store.dispatch('Streams/getStreams', true);
    track(EVENTS.VOD_FOLDER_CREATED, {});
    notify({ group: 'success', text: 'Folder created successfully' });
    creatingFirstFolder.value = false;
  } catch (err) {
    notify({ group: 'error', title: 'Couldn’t create folder', text: err?.message || '' });
  }
  firstFolderProcessing.value = false;
};

</script>

<template>
  <div class="container detail-shell px-4">
    <PageHeader
      v-if="!noAccess && (loadingStatus || !userloaded || vodStreams.length === 0)"
      title="Content Library"
      subtitle="Host and manage your on-demand video library."
    />
    <Spinner v-if="loadingStatus || !userloaded" text="Loading your library…"
      classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)" spinner-size="15px" />
    <div v-else-if="!noAccess">
      <section>
        <div v-if="isVodTrial && !isVodPaid && !hasPaidPlan">
          <p v-if="vodTrialExpiredIn >= 0">
            Your trial plan will end in {{ vodTrialExpiredIn }} days.Please
            <router-link to="/subscribe?category=live">
              Upgrade
            </router-link>
            to extend your service.
          </p>
          <p v-else>
            Your trial plan has ended.Please
            <router-link to="/subscribe?category=live">
              Upgrade
            </router-link>
            to extend your service.
          </p>
        </div>
        <div v-if="
          (!emptyVodSub || !isVodTrial) && isVodPaid && vodTrialExpiredIn < 0
        ">
          <p>
            Your plan has ended.Please
            <router-link to="/subscribe?category=live">
              Upgrade
            </router-link>
            to continue.
          </p>
        </div>
      </section>

      <VODFolderGrid v-if="vodStreams.length > 0" :folders="vodStreams" :storage-used-bytes="storageUsedBytes"
        :max-storage-gb="maxVodStorage" :can-manage="checkManagePermissions()" :disabled="vodTrialExpiredIn < 0"
        @new-folder="showVodAddModal" @refresh="onFoldersRefresh" />

      <template v-else>
        <EmptyState v-if="
          isVodTrial ||
          userSubscription.maxVodItems > 0 ||
          userSubscription.maxVodStorage > 0 ||
          allVodStreams.length > 0
        " min-height="55vh" title="Host your first videos"
          subtitle="Keep your videos in folders you can share as links or embed on your site.">
          <template #icon>
            <icon-base :width="28" :height="28" fill="none">
              <icon-folder />
            </icon-base>
          </template>
          <template #actions>
            <!-- Inline creation: the button morphs into a name input, no modal -->
            <div v-if="creatingFirstFolder"
              class="w-[280px] rounded-xl border-2 border-dashed border-main/70 p-3.5 flex flex-col gap-2">
              <FormInput ref="firstFolderInput" v-model="firstFolderName" placeholder="Folder name"
                @keyup.enter="confirmFirstFolder" @keyup.esc="cancelFirstFolder" />
              <div class="flex items-center justify-end gap-1.5">
                <FormButton type="secondary" size="sm" @click="cancelFirstFolder">Cancel</FormButton>
                <FormButton type="main" size="sm" :disabled="!firstFolderName.trim() || firstFolderProcessing"
                  @click="confirmFirstFolder">
                  {{ firstFolderProcessing ? 'Creating…' : 'Create' }}
                </FormButton>
              </div>
            </div>
            <FormButton v-else
              :id="activeTeam && !checkManagePermissions ? 'member-disabled_new_folder' : ''"
              :disabled="vodTrialExpiredIn < 0" @click="startFirstFolder">
              <icon-base class="!mr-1">
                <icon-plus />
              </icon-base>
              Create your first folder
            </FormButton>
          </template>
        </EmptyState>

        <EmptyState v-else min-height="55vh" title="Try Video Hosting free for 7 days"
          subtitle="Host your videos on demand and share or embed them anywhere. No commitment.">
          <template #icon>
            <icon-base :width="28" :height="28" fill="none">
              <icon-hosting />
            </icon-base>
          </template>
          <template #actions>
            <FormButton @click="activateTrial">
              Activate free trial
            </FormButton>
          </template>
        </EmptyState>
      </template>
    </div>
    <div class="mt-6 text-center" v-else-if="!loadingStatus && userloaded">
        <h4 class="text-dark-9 text-s-xl text-500 mb-1">You don’t have permission to access this.</h4>
        <p class="text-dark-6 text-s-m">Contact the team owner at <a class="text-dark-6 text-s-m text-underline"
            :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
    </div>
    <add-vod-modal @new-channel="onNewStream" />
    <!-- A paused or removed team membership is announced once, by the sidebar's
         access modal; a second copy here (same modal id) only stacked behind it. -->
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
